import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { parseGuidePrefill } from '@bookora/shared';
import { isApiError } from '@bookora/ui';

import type {
  GuideTipActionDtoSurface,
  GuideTipCtaResponse,
  GuideTipResponse,
} from '@/api/generated/model';
import { startTour, type TourKey } from '@/guidance';
// The registry itself, not the barrel: screens' tests mock `@/guidance`.
import { isTourKey } from '@/guidance/tours/registry';
import { toast } from '@/lib/feedback';
import { isPanelPath } from '@/lib/panel-path';
import { REASON } from '@/lib/reasons';

import { useGuideCreateDraft, useGuideTipAction, useRefetchTips } from './api';
import { CTA, READ_ONLY_REASON } from './copy';
import type { GuideRouterState } from './guide-prefill';
import { useClinicReadOnly } from './use-clinic-read-only';

/**
 * What a tip's button does, in one place for every surface.
 *
 *   NAVIGATE  records ACCEPT (not awaited) and opens the panel path; a
 *             prefill travels as router state (`GuideRouterState`) for the
 *             page to read where it consumes `?new=1`.
 *   DRAFT     asks the server for the tip's DRAFT campaign (one per clinic and
 *             tip, reused by every colleague) and opens it; the server marks
 *             the tip accepted. 409 = the tip went stale under us → say so and
 *             re-read; 403 → re-read (the list was out of date about rights).
 *   COPY      copies the tip's text for the clinic's messenger.
 *   TOUR      starts that section's tour at the named step.
 *   MARK_DONE the manual «انجام دادم» for tips nothing in Bookora can observe.
 *
 * A path that is not a same-origin panel path is refused (the server's data,
 * `isPanelPath`); a TOUR the panel does not ship is not drawn at all.
 *
 * While a support session views the panel as this user (`readOnly`), a
 * NAVIGATE still works — it only moves around — but DRAFT and MARK_DONE, which
 * would write, are blocked with `READ_ONLY_REASON`.
 *
 * While the CLINIC is billing read-only (`useClinicReadOnly`), every CTA that
 * leads to a write is blocked with `REASON.CLINIC_READ_ONLY` — a draft, a
 * manual done, and a NAVIGATE that opens a form (`?new=1`, or a prefill). A
 * CTA to «تنظیمات › اشتراک» stays live: renewing is the way out.
 */

/**
 * The tours this panel ships, from the registry itself — a planned tour with
 * no steps yet is not linkable; a TOUR CTA naming anything else is not drawn.
 */
export function isKnownTour(key: string | null | undefined): key is TourKey {
  return typeof key === 'string' && isTourKey(key);
}

/** A NAVIGATE that opens a form to fill (and so ends in a write). */
function opensForm(cta: GuideTipCtaResponse): boolean {
  if (cta.kind !== 'NAVIGATE') return false;
  if (cta.prefill) return true;
  const query = (cta.path ?? '').split('?')[1] ?? '';
  return new URLSearchParams(query.split('#')[0]).get('new') === '1';
}

/** A CTA to the subscription screen: the one way out of read-only, never blocked. */
function isSubscriptionCta(cta: GuideTipCtaResponse): boolean {
  if (cta.kind !== 'NAVIGATE') return false;
  const [pathname, query = ''] = (cta.path ?? '').split('#')[0].split('?');
  if (pathname === '/settings/subscription') return true;
  return pathname === '/settings' && new URLSearchParams(query).get('tab') === 'subscription';
}

/** Whether a CTA can be drawn at all on this panel. */
export function isRenderableCta(cta: GuideTipCtaResponse): boolean {
  switch (cta.kind) {
    case 'NAVIGATE':
      return isPanelPath(cta.path);
    case 'TOUR':
      return isKnownTour(cta.tourKey);
    case 'COPY':
      return Boolean(cta.copyText);
    case 'DRAFT':
    case 'MARK_DONE':
      return true;
    default:
      return false;
  }
}

export interface GuideCtaOptions {
  surface: GuideTipActionDtoSurface;
  readOnly: boolean;
  /**
   * The surface's own MARK_DONE (records it and offers the undo row, see
   * `useTipActions`). Without one, the action is sent directly.
   */
  onMarkDone?: (tip: GuideTipResponse) => void;
}

export interface GuideCtaRunner {
  /**
   * `secondary`: one of the tip's extra links. It goes where it says and
   * records nothing — only the tip's own button (or «انجام دادم») answers the
   * tip. A secondary click once counted as ACCEPT and hid the whole tip.
   */
  run: (tip: GuideTipResponse, cta: GuideTipCtaResponse, options?: { secondary?: boolean }) => void;
  /** The DRAFT being prepared for this tip. */
  pendingItemKey: string | null;
  /** Why this CTA cannot be used right now, or `null`. */
  reasonFor: (cta: GuideTipCtaResponse) => string | null;
}

export function useGuideCta({ surface, readOnly, onMarkDone }: GuideCtaOptions): GuideCtaRunner {
  const navigate = useNavigate();
  const action = useGuideTipAction();
  const draft = useGuideCreateDraft();
  const refetch = useRefetchTips();
  const [pendingItemKey, setPendingItemKey] = useState<string | null>(null);
  const clinicReadOnly = useClinicReadOnly();
  const { mutate: act } = action;
  const { mutateAsync: createDraft } = draft;

  const reasonFor = useCallback(
    (cta: GuideTipCtaResponse): string | null => {
      const writes = cta.kind === 'DRAFT' || cta.kind === 'MARK_DONE';
      if (readOnly && writes) return READ_ONLY_REASON;
      if (clinicReadOnly && !isSubscriptionCta(cta) && (writes || opensForm(cta))) {
        return REASON.CLINIC_READ_ONLY;
      }
      return null;
    },
    [readOnly, clinicReadOnly],
  );

  const accept = useCallback(
    (tip: GuideTipResponse, ctaKind: 'NAVIGATE' | 'COPY' | 'TOUR') => {
      if (readOnly) return;
      act({ itemKey: tip.itemKey, action: 'ACCEPT', surface, ctaKind });
    },
    [act, readOnly, surface],
  );

  const run = useCallback(
    (tip: GuideTipResponse, cta: GuideTipCtaResponse, options?: { secondary?: boolean }): void => {
      if (reasonFor(cta)) return;
      const answer = (kind: 'NAVIGATE' | 'COPY' | 'TOUR'): void => {
        if (!options?.secondary) accept(tip, kind);
      };
      switch (cta.kind) {
        case 'NAVIGATE': {
          if (!isPanelPath(cta.path)) {
            console.warn(`[guide] refused a non-panel path for «${tip.itemKey}»`);
            return;
          }
          answer('NAVIGATE');
          const prefill = parseGuidePrefill(cta.prefill ?? null);
          const state: GuideRouterState | undefined = prefill
            ? { guidePrefill: prefill, guideTip: tip.itemKey }
            : undefined;
          void navigate(cta.path, state ? { state } : undefined);
          return;
        }
        case 'DRAFT': {
          if (pendingItemKey) return;
          setPendingItemKey(tip.itemKey);
          createDraft(tip.itemKey).then(
            (result) => {
              setPendingItemKey(null);
              refetch();
              if (isPanelPath(result.path)) void navigate(result.path);
            },
            (error: unknown) => {
              setPendingItemKey(null);
              if (isApiError(error) && error.statusCode === 409) {
                toast.info(CTA.stale);
                refetch();
              } else if (isApiError(error) && error.statusCode === 403) {
                refetch();
              } else if (!(isApiError(error) && error.statusCode === 402)) {
                // A 402 is the read-only watcher's to explain (BillingRefusalWatcher).
                toast.error(error);
              }
            },
          );
          return;
        }
        case 'COPY': {
          const text = cta.copyText ?? '';
          answer('COPY');
          // `clipboard` is absent outside a secure context, whatever the types say.
          const clipboard = navigator.clipboard as Clipboard | undefined;
          if (!clipboard) {
            toast.warning(CTA.copyFailed);
            return;
          }
          clipboard.writeText(text).then(
            () => toast.success(CTA.copied),
            () => toast.warning(CTA.copyFailed),
          );
          return;
        }
        case 'TOUR': {
          if (!isKnownTour(cta.tourKey)) return;
          answer('TOUR');
          startTour(cta.tourKey, {
            source: 'tip',
            ...(cta.startAtStepId ? { startAtStepId: cta.startAtStepId } : {}),
          });
          return;
        }
        case 'MARK_DONE': {
          if (onMarkDone) onMarkDone(tip);
          else act({ itemKey: tip.itemKey, action: 'MARK_DONE', surface });
          return;
        }
        default:
          return;
      }
    },
    [accept, act, createDraft, navigate, onMarkDone, pendingItemKey, reasonFor, refetch, surface],
  );

  return { run, pendingItemKey, reasonFor };
}
